import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Calendar, Clock, MapPin, User, Loader2, Check, History, ThumbsUp, MinusCircle, ThumbsDown } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import api from '../../lib/api';

/**
 * CreateVisitModal v2
 * ====================
 * Unifie :
 *  - Mode "Planifier" (visite future)
 *  - Mode "Logger" (visite passée avec feedback en un coup) → débloque post_visit_return_rate et visits_no_return
 */
export default function CreateVisitModal({
    isOpen,
    onClose,
    mandateId,
    propertyAddress,
    onSuccess,
    initialMode = 'future'
}) {
    const [mode, setMode] = useState(initialMode);

    React.useEffect(() => {
        if (isOpen) setMode(initialMode);
    }, [isOpen, initialMode]);

    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        visitorName: '',
        visitorPhone: '',
        visitorEmail: '',
        date: '',
        time: '',
        notes: '',
        feedback: 'interested',
        feedbackComments: '',
        nextSteps: '',
    });

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.visitorName || !formData.date || !formData.time) {
            toast.error('Nom visiteur, date et heure obligatoires');
            return;
        }

        setLoading(true);

        try {
            const startDateTime = new Date(`${formData.date}T${formData.time}`);

            // 1. Create visit
            const visitResponse = await api.post('/visits/', {
                mandate_id: mandateId,
                scheduled_date: startDateTime.toISOString(),
                scheduled_time: formData.time,
                visitor_name: formData.visitorName,
                visitor_phone: formData.visitorPhone,
                visitor_email: formData.visitorEmail,
                notes: formData.notes
            });

            const visitId = visitResponse.data.id || visitResponse.data.visit_id;

            // 2. If mode=past → immediately submit report (marks completed)
            if (mode === 'past' && visitId) {
                try {
                    await api.post(`/visits/${visitId}/report`, {
                        visitor_feedback: formData.feedback,
                        visitor_comments: formData.feedbackComments || null,
                        next_steps: formData.nextSteps || null,
                    });
                    toast.success('Visite enregistrée avec feedback');
                } catch (reportErr) {
                    toast.success('Visite créée (feedback échoué, à ajouter manuellement)');
                }
            } else if (mode === 'future') {
                // Optional: Google Calendar sync
                try {
                    const calStatus = await api.get('/google-calendar/status');
                    if (calStatus.data.connected) {
                        const endDt = new Date(startDateTime.getTime() + 60 * 60 * 1000);
                        await api.post('/google-calendar/events', {
                            title: `Visite - ${formData.visitorName}`,
                            description: `Visite du bien: ${propertyAddress}\nVisiteur: ${formData.visitorName}`,
                            start: startDateTime.toISOString(),
                            end: endDt.toISOString(),
                            location: propertyAddress,
                            visit_id: visitId,
                            mandate_id: mandateId
                        });
                        toast.success('Visite planifiée + ajoutée à Google Calendar');
                    } else {
                        toast.success('Visite planifiée');
                    }
                } catch (calErr) {
                    toast.success('Visite planifiée');
                }
            }

            setFormData({
                visitorName: '', visitorPhone: '', visitorEmail: '',
                date: '', time: '', notes: '',
                feedback: 'interested', feedbackComments: '', nextSteps: '',
            });

            onSuccess?.();
            onClose();

        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la création');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
                onClick={onClose}
            >
                <motion.div
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.95, opacity: 0 }}
                    className="bg-slate-900 rounded-xl shadow-2xl w-full max-w-lg border border-border overflow-hidden max-h-[90vh] overflow-y-auto"
                    onClick={(e) => e.stopPropagation()}
                    data-testid="visit-modal"
                >
                    <div className="px-6 py-4 border-b border-border flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-purple-500/20 rounded-lg">
                                <Calendar className="w-5 h-5 text-purple-400" />
                            </div>
                            <h2 className="text-lg font-semibold text-white">
                                {mode === 'future' ? 'Planifier une visite' : 'Enregistrer une visite passée'}
                            </h2>
                        </div>
                        <button onClick={onClose} className="p-1 hover:bg-muted rounded-lg">
                            <X className="w-5 h-5 text-muted-foreground" />
                        </button>
                    </div>

                    {/* Mode toggle */}
                    <div className="px-6 py-3 bg-card/50 border-b border-border">
                        <div className="grid grid-cols-2 gap-1 p-1 bg-background rounded-lg" data-testid="visit-mode-toggle">
                            <button
                                type="button"
                                onClick={() => setMode('future')}
                                className={`flex items-center justify-center gap-2 py-2 px-3 rounded-md text-xs font-bold transition-all ${
                                    mode === 'future' ? 'bg-purple-500/20 text-purple-300' : 'text-muted-foreground hover:text-foreground'
                                }`}
                                data-testid="mode-future"
                            >
                                <Calendar className="w-3.5 h-3.5" /> À venir
                            </button>
                            <button
                                type="button"
                                onClick={() => setMode('past')}
                                className={`flex items-center justify-center gap-2 py-2 px-3 rounded-md text-xs font-bold transition-all ${
                                    mode === 'past' ? 'bg-purple-500/20 text-purple-300' : 'text-muted-foreground hover:text-foreground'
                                }`}
                                data-testid="mode-past"
                            >
                                <History className="w-3.5 h-3.5" /> Déjà effectuée
                            </button>
                        </div>
                        <p className="text-[10px] text-muted-foreground mt-1.5 px-1">
                            {mode === 'future' ? 'Planification avec rappel + calendrier' : 'Logger une visite passée avec feedback (KPIs visites)'}
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="p-6 space-y-4">
                        {propertyAddress && (
                            <div className="p-3 bg-card rounded-lg flex items-start gap-2">
                                <MapPin className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                                <span className="text-sm text-muted-foreground">{propertyAddress}</span>
                            </div>
                        )}

                        <div className="space-y-2">
                            <Label className="text-white/80">Nom du visiteur <span className="text-red-400">*</span></Label>
                            <div className="relative">
                                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <Input
                                    value={formData.visitorName}
                                    onChange={(e) => setFormData(prev => ({ ...prev, visitorName: e.target.value }))}
                                    placeholder="Jean Dupont"
                                    className="pl-10 bg-card border-border text-white"
                                    data-testid="visit-visitor-name"
                                    required
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="text-white/80">Téléphone</Label>
                                <Input
                                    type="tel"
                                    value={formData.visitorPhone}
                                    onChange={(e) => setFormData(prev => ({ ...prev, visitorPhone: e.target.value }))}
                                    placeholder="06 12 34 56 78"
                                    className="bg-card border-border text-white"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-white/80">Email</Label>
                                <Input
                                    type="email"
                                    value={formData.visitorEmail}
                                    onChange={(e) => setFormData(prev => ({ ...prev, visitorEmail: e.target.value }))}
                                    placeholder="email@exemple.com"
                                    className="bg-card border-border text-white"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="text-white/80">Date <span className="text-red-400">*</span></Label>
                                <div className="relative">
                                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                    <Input
                                        type="date"
                                        value={formData.date}
                                        onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                                        className="pl-10 bg-card border-border text-white"
                                        max={mode === 'past' ? new Date().toISOString().slice(0, 10) : undefined}
                                        min={mode === 'future' ? new Date().toISOString().slice(0, 10) : undefined}
                                        data-testid="visit-date"
                                        required
                                    />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label className="text-white/80">Heure <span className="text-red-400">*</span></Label>
                                <div className="relative">
                                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                    <Input
                                        type="time"
                                        value={formData.time}
                                        onChange={(e) => setFormData(prev => ({ ...prev, time: e.target.value }))}
                                        className="pl-10 bg-card border-border text-white"
                                        data-testid="visit-time"
                                        required
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Feedback (mode past only) */}
                        {mode === 'past' && (
                            <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                className="space-y-3 p-3 rounded-xl bg-purple-500/5 border border-purple-500/20"
                                data-testid="visit-feedback-block"
                            >
                                <Label className="text-white/80 font-bold text-xs uppercase tracking-wider">Feedback acquéreur</Label>
                                <div className="grid grid-cols-3 gap-2">
                                    {[
                                        { k: 'interested', label: 'Intéressé', icon: ThumbsUp, color: 'green' },
                                        { k: 'thinking', label: 'Réflexion', icon: MinusCircle, color: 'amber' },
                                        { k: 'not_interested', label: 'Pas intéressé', icon: ThumbsDown, color: 'red' },
                                    ].map(({ k, label, icon: Icon, color }) => (
                                        <button
                                            key={k}
                                            type="button"
                                            onClick={() => setFormData(prev => ({ ...prev, feedback: k }))}
                                            className={`p-2 rounded-lg border-2 flex flex-col items-center gap-1 text-[11px] font-bold transition-all ${
                                                formData.feedback === k
                                                    ? color === 'green' ? 'bg-green-500/20 border-green-500 text-green-300'
                                                      : color === 'amber' ? 'bg-amber-500/20 border-amber-500 text-amber-300'
                                                      : 'bg-red-500/20 border-red-500 text-red-300'
                                                    : 'bg-background border-white/10 text-muted-foreground hover:border-white/20'
                                            }`}
                                            data-testid={`feedback-${k}`}
                                        >
                                            <Icon className="w-4 h-4" />
                                            {label}
                                        </button>
                                    ))}
                                </div>
                                <div>
                                    <Label className="text-white/70 text-xs">Commentaire / retour</Label>
                                    <textarea
                                        value={formData.feedbackComments}
                                        onChange={(e) => setFormData(prev => ({ ...prev, feedbackComments: e.target.value }))}
                                        placeholder="Ex: coup de cœur, attendent validation banque…"
                                        className="w-full px-3 py-2 bg-card border border-border rounded-md text-white placeholder:text-muted-foreground resize-none text-sm"
                                        rows={2}
                                        data-testid="visit-feedback-comment"
                                    />
                                </div>
                                <div>
                                    <Label className="text-white/70 text-xs">Prochaine étape (optionnel)</Label>
                                    <Input
                                        value={formData.nextSteps}
                                        onChange={(e) => setFormData(prev => ({ ...prev, nextSteps: e.target.value }))}
                                        placeholder="Ex: relance J+3, envoyer offre type"
                                        className="bg-card border-border text-white"
                                        data-testid="visit-next-steps"
                                    />
                                </div>
                            </motion.div>
                        )}

                        <div className="space-y-2">
                            <Label className="text-white/80">Notes</Label>
                            <textarea
                                value={formData.notes}
                                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                                placeholder="Informations complémentaires…"
                                className="w-full px-3 py-2 bg-card border border-border rounded-md text-white placeholder:text-muted-foreground resize-none"
                                rows={2}
                            />
                        </div>

                        <div className="flex gap-3 pt-2">
                            <Button type="button" variant="outline" onClick={onClose} className="flex-1 border-border text-muted-foreground">
                                Annuler
                            </Button>
                            <Button
                                type="submit"
                                disabled={loading}
                                className="flex-1 bg-gradient-to-r from-purple-600 to-purple-500 text-white"
                                data-testid="visit-submit-btn"
                            >
                                {loading ? (
                                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enregistrement…</>
                                ) : (
                                    <><Check className="w-4 h-4 mr-2" /> {mode === 'past' ? 'Enregistrer + feedback' : 'Planifier'}</>
                                )}
                            </Button>
                        </div>
                    </form>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}
